import type { Locale, Suggestion } from "../../../shared/types";
import { useCopy } from "../lib/copy";

interface SuggestionsProps {
  locale: Locale;
  /** Already capped at four by the server. Empty renders nothing at all. */
  suggestions: Suggestion[];
  /** Sends the chip as the founder's own turn. The row clears itself on the way out. */
  onPick: (suggestion: Suggestion) => void;
}

/**
 * What the founder could say next, as a row of chips just above the dock.
 *
 * This exists because at rest the product is a blob, a blank status line and an
 * empty text field, and a founder who does not know what to say says nothing. It is
 * an offer, not a menu: the chips are sentences in the founder's own voice, they are
 * never more than four, and they are gone the moment the founder starts typing —
 * which is the caller's decision, not this component's.
 *
 * `kind` is deliberately not rendered. An answer and a probe look identical, because
 * labelling them would turn a row of things you might say into a taxonomy of things
 * the software offers.
 */
export default function Suggestions({ locale, suggestions, onPick }: SuggestionsProps) {
  const copy = useCopy(locale);

  // The container renders whether or not there are chips: its slot in the stack
  // above the dock is reserved permanently, so chips appearing and vanishing can
  // never move the dock, the status line or the transcript's floor (the layout
  // stability contract). Empty, it is invisible and silent — no role, no label.
  const offering = suggestions.length > 0;

  return (
    <div
      className="suggest"
      role={offering ? "group" : undefined}
      aria-label={offering ? copy.suggestionsLabel : undefined}
    >
      {suggestions.map((suggestion) => (
        <button
          key={suggestion.id}
          type="button"
          className="suggest__chip"
          onClick={() => onPick(suggestion)}
        >
          {suggestion.text}
        </button>
      ))}
    </div>
  );
}
